import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, Telescope } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import { Section } from '@/components/Section';
import type { TimeZoneMode } from '@/lib/format';
import {
  writeLookupToHash,
  type Bootstrap,
  type LookupParams,
} from '@/lib/bootstrap';
import type { BetlensResult } from '@/types';
import { CommandBar } from './components/CommandBar';
import { HintsPanel } from './components/HintsPanel';
import { ResultsToolbar } from './components/ResultsToolbar';
import { ReconciliationMatrix } from './components/ReconciliationMatrix';
import { RoundSection } from './components/sections/RoundSection';
import { SourceRail } from './components/SourceRail';
import { Timeline } from './components/Timeline';
import { VerdictStrip } from './components/VerdictStrip';
import {
  ClickhouseSections,
  CurrencySection,
  DeliverySection,
  FreebetsSection,
  GameBetSection,
  LogsSection,
  RawSection,
  RoundDetailsSection,
  RtpSection,
  SessionsSection,
  TracesSection,
  TransactionsSection,
} from './components/sections/Sections';
import { ApiError, fetchBet, fetchClients, toInput } from './lib/api';

/**
 * Bet Inspection — the whole story of one bet.
 *
 * Self-contained: it owns its lookup bar, its query, and the assembled bundle.
 * The shell hands it the bootstrap context (api/token) and the global tz; theme
 * and navigation are the shell's concern, not this feature's.
 */
export function BetInspection({
  boot,
  tz,
  focus,
}: {
  boot: Bootstrap;
  tz: TimeZoneMode;
  /** A lookup handed in from another view (e.g. a Transactions row). `nonce` bumps per open. */
  focus?: { lookup: LookupParams; nonce: number } | null;
}) {
  // The selectable projects for the lookup dropdown. Cached for the session —
  // the client list barely changes and a lookup shouldn't wait on it.
  const clients = useQuery({
    queryKey: ['clients', boot.api],
    queryFn: ({ signal }) => fetchClients({ api: boot.api!, token: boot.token, signal }),
    enabled: Boolean(boot.api),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const lookup = useMutation<BetlensResult, Error, LookupParams>({
    mutationFn: async (params) =>
      fetchBet({ api: boot.api!, token: boot.token, input: toInput(params) }),
    onSuccess: (_, params) => writeLookupToHash(params),
  });

  const runLookup = useCallback((params: LookupParams) => lookup.mutate(params), [lookup]);

  // A deep-linked bet in the hash runs on load, so a shared URL lands the next
  // person on the same bet with no retyping.
  const auto = boot.lookup;
  useEffect(() => {
    if (auto && boot.api) lookup.mutate(auto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(auto), boot.api]);

  // Another view linked into this bet — run it. `nonce` re-fires even for the
  // same bet, and re-keys the command bar so its fields reflect the new lookup.
  useEffect(() => {
    if (focus && boot.api) lookup.mutate(focus.lookup);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.nonce, boot.api]);

  const result = lookup.data;
  const bundle = result?.bundle;
  const sectionProps = useMemo(() => (bundle ? { bundle, tz } : null), [bundle, tz]);

  return (
    <>
      <CommandBar
        key={focus?.nonce ?? 'boot'}
        initial={focus?.lookup ?? boot.lookup}
        loading={lookup.isPending}
        clients={clients.data ?? []}
        clientsLoading={clients.isLoading}
        clientsError={clients.isError}
        onLookup={runLookup}
        api={boot.api ?? ''}
        token={boot.token}
      />

      {lookup.isPending && <Scanning />}
      {lookup.isError && <Failure error={lookup.error} />}
      {!lookup.isPending && !lookup.isError && !bundle && <Idle />}

      {bundle && sectionProps && result && (
        <main className="rise">
          <ResultsToolbar input={lookup.variables ?? null} result={result} />
          <VerdictStrip bundle={bundle} />
          <HintsPanel hints={result.hints} />
          <SourceRail bundle={bundle} />

          <RoundSection
            bundle={bundle}
            tz={tz}
            onLookupRound={(roundId) =>
              runLookup({
                play_service_id: bundle.resolved.play_service_id,
                game_uuid: bundle.resolved.game_uuid,
                round_id: roundId,
              })
            }
          />

          <Section id="section-timeline" title="Timeline" term="timezone_skew">
            <Timeline bundle={bundle} tz={tz} />
          </Section>

          <Section id="section-reconciliation" title="Reconciliation" term="reconciliation">
            <ReconciliationMatrix bundle={bundle} tz={tz} />
          </Section>

          <TransactionsSection {...sectionProps} />
          <GameBetSection {...sectionProps} />
          <RoundDetailsSection {...sectionProps} />
          <DeliverySection {...sectionProps} />
          <SessionsSection {...sectionProps} />
          <FreebetsSection {...sectionProps} />
          <ClickhouseSections {...sectionProps} />
          <LogsSection {...sectionProps} />
          <TracesSection {...sectionProps} />
          <RtpSection {...sectionProps} />
          <CurrencySection {...sectionProps} />
          <RawSection bundle={bundle} />

          <footer className="px-4 py-6 text-center text-[11px] text-text-faint">
            betlens · every source above reports its own state — an unreachable source
            is never shown as empty
          </footer>
        </main>
      )}
    </>
  );
}


function Scanning() {
  return (
    <div className="instrument-bg flex min-h-[60vh] flex-col items-center justify-center gap-4">
      <div className="lens-scan relative h-px w-[220px] overflow-hidden bg-hairline" />
      <p className="font-condensed text-[11px] uppercase tracking-[0.2em] text-text-dim">
        collecting the whole story of this bet
      </p>
    </div>
  );
}

function Idle() {
  return (
    <div className="instrument-bg flex min-h-[60vh] flex-col items-center justify-center gap-3 px-6 text-center">
      <Telescope size={22} className="text-text-faint" />
      <p className="max-w-[420px] text-[13px] leading-relaxed text-text-dim">
        Look up a bet to pull its whole story — every transaction, retry, session and
        log we hold — and see what the rules flag.
      </p>
      <p className="font-mono text-[11px] text-text-faint">
        by bet · by trx · cycle hash · tr hash — each scoped to one operator
      </p>
    </div>
  );
}

function Failure({ error }: { error: Error }) {
  const status = error instanceof ApiError ? error.status : 0;
  const isNotFound = status === 404;
  const isForbidden = status === 403;

  return (
    <div className="flex min-h-[40vh] items-start justify-center px-6 pt-16">
      <div className="flex max-w-[520px] gap-3 border border-crit/40 bg-crit-bg p-4">
        <AlertCircle size={16} className="mt-[2px] shrink-0 text-crit" />
        <div>
          <div className="mb-1 font-condensed text-[11px] font-semibold uppercase tracking-wider text-crit">
            {isForbidden ? 'Not permitted' : isNotFound ? 'Bet not found' : 'Lookup failed'}
          </div>
          <p className="text-[12.5px] leading-relaxed text-text">{error.message}</p>
          {isForbidden && (
            <p className="mt-2 text-[12px] text-text-dim">
              This operator is outside the set you're allowed to view. Pick one from the
              project list — it only shows operators you have access to.
            </p>
          )}
          {isNotFound && (
            <p className="mt-2 text-[12px] text-text-dim">
              Check the operator matches the bet — every lookup is scoped to one
              operator, so a valid bet id under the wrong one looks missing.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
